import React, { useState, useEffect, useCallback } from 'react';
import supabase from '../config/supabase';
import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';

const WEBHOOK_ENDPOINT = `${SUPABASE_URL}/functions/v1/purchase-order-webhook`;

const RECOMMENDED_EVENTS = [
  'email.sent',
  'email.delivered',
  'email.bounced',
  'email.complained',
  'email.opened',
  'email.clicked',
];

function timeAgo(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function fmtEventType(type) {
  const map = {
    'email.sent': 'Sent',
    'email.delivered': 'Delivered',
    'email.bounced': 'Bounced',
    'email.complained': 'Complained',
    'email.opened': 'Opened',
    'email.clicked': 'Clicked',
  };
  return map[type] || type;
}

function eventBadgeStyle(type) {
  if (type === 'email.bounced' || type === 'email.complained') {
    return { backgroundColor: '#FEE2E2', color: '#B91C1C', borderRadius: 4, padding: '2px 7px', fontSize: 12, fontWeight: 600 };
  }
  if (type === 'email.opened' || type === 'email.clicked') {
    return { backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '2px 7px', fontSize: 12, fontWeight: 600 };
  }
  if (type === 'email.delivered') {
    return { backgroundColor: '#DBEAFE', color: '#1E40AF', borderRadius: 4, padding: '2px 7px', fontSize: 12, fontWeight: 600 };
  }
  return { backgroundColor: '#F3F4F6', color: '#374151', borderRadius: 4, padding: '2px 7px', fontSize: 12, fontWeight: 600 };
}

export default function EmailDeliverySettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedEvents, setCopiedEvents] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setError('You must be signed in to view email delivery status.');
        setLoading(false);
        return;
      }
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/webhook-status`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_KEY,
        },
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.error || `Server error (${resp.status})`);
      }
      setStatus(await resp.json());
    } catch (err) {
      setError(err.message || 'Failed to load webhook status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const copyToClipboard = (text, onSuccess) => {
    if (!navigator.clipboard) {
      try {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        onSuccess();
      } catch (_) {}
      return;
    }
    navigator.clipboard.writeText(text).then(onSuccess).catch(() => {});
  };

  const copyUrl = () => {
    copyToClipboard(WEBHOOK_ENDPOINT, () => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  const copyEvents = () => {
    copyToClipboard(RECOMMENDED_EVENTS.join('\n'), () => {
      setCopiedEvents(true);
      setTimeout(() => setCopiedEvents(false), 2000);
    });
  };

  const isConnected = status?.last_event_at != null;

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="mb-0">Email Delivery</h5>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={loadStatus}
          disabled={loading}
          title="Refresh status"
        >
          <i className="feather icon-refresh-cw me-1"></i>
          Refresh
        </button>
      </div>

      <div className="card-body">
        {/* Connection status */}
        <div className="mb-4">
          {loading ? (
            <div className="d-flex align-items-center gap-2" style={{ color: '#8B7B73' }}>
              <div className="spinner-border spinner-border-sm" role="status"></div>
              <span>Checking connection…</span>
            </div>
          ) : error ? (
            <div className="alert alert-danger mb-0 py-2">
              <i className="feather icon-alert-circle me-2"></i>
              {error}
            </div>
          ) : (
            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded"
              style={{
                background: isConnected ? '#D1FAE5' : '#FEF3C7',
                color: isConnected ? '#065F46' : '#92400E',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: isConnected ? '#10B981' : '#F59E0B',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              {isConnected
                ? `Connected — last event received ${timeAgo(status.last_event_at)}`
                : 'Not connected yet — no events received'}
            </div>
          )}
        </div>

        {/* Missing secret warning */}
        {!loading && !error && status && !status.secret_configured && (
          <div className="alert alert-warning mb-4">
            <i className="feather icon-alert-triangle me-2"></i>
            <strong>RESEND_WEBHOOK_SECRET is not set.</strong> The webhook handler will reject all
            incoming events until this secret is configured. Ask your developer to save the signing
            secret from Resend as the <code>RESEND_WEBHOOK_SECRET</code> environment variable.
          </div>
        )}

        {/* Not-connected advisory when secret IS set but no events and no recent rejections */}
        {!loading && !error && status && status.secret_configured && !isConnected
          && !(status.rejection_count_24h > 0) && (
          <div className="alert alert-info mb-4 py-2" style={{ fontSize: 13 }}>
            <i className="feather icon-info me-2"></i>
            <strong>The signing secret is configured, but no events have been received yet.</strong>{' '}
            To complete the setup, verify in your Resend dashboard that:
            <ul className="mb-0 mt-1" style={{ paddingLeft: 20 }}>
              <li>A webhook endpoint pointing to the URL below has been created.</li>
              <li>All six event types (sent, delivered, bounced, complained, opened, clicked) are subscribed.</li>
              <li>The signing secret shown in Resend matches <code>RESEND_WEBHOOK_SECRET</code> exactly (no extra spaces).</li>
            </ul>
            Once configured, send a new purchase order — events should appear here within a minute.
          </div>
        )}

        {/* Signature rejection warning — events ARE arriving but being rejected */}
        {!loading && !error && status && status.rejection_count_24h > 0 && (
          <div className="alert alert-danger mb-4 py-2" style={{ fontSize: 13 }}>
            <i className="feather icon-x-circle me-2"></i>
            <strong>
              {status.rejection_count_24h} webhook call{status.rejection_count_24h !== 1 ? 's were' : ' was'} rejected
              system-wide in the last 24 hours.
            </strong>{' '}
            {status.last_rejection?.status === 'rejected_config'
              ? 'The RESEND_WEBHOOK_SECRET environment variable is missing or empty on the server.'
              : 'Resend is reaching the endpoint but the signing secret does not match. '
                + 'Copy the exact signing secret from your Resend webhook settings and update RESEND_WEBHOOK_SECRET — '
                + 'even a single extra space will cause every event to be rejected.'}
            {status.last_rejection?.at && (
              <> Last rejection: {new Date(status.last_rejection.at).toLocaleString()}.</>
            )}
          </div>
        )}

        {/* Setup instructions */}
        <h6 className="mb-3" style={{ fontWeight: 700 }}>Setup Instructions</h6>
        <ol className="mb-4" style={{ lineHeight: 1.8 }}>
          <li>Copy the webhook URL below and keep it handy.</li>
          <li>
            In Resend, go to <strong>Webhooks → Add webhook</strong>, paste the URL, and select
            all of the event types listed below.
          </li>
          <li>
            Copy the signing secret that Resend shows you after saving, and ask your developer to
            store it as the <code>RESEND_WEBHOOK_SECRET</code> environment variable. If you manage
            environment variables yourself, you can set it directly in the Replit secrets panel.
          </li>
          <li>
            Return to this page and click <strong>Refresh</strong>. Once any new purchase-order
            email is sent, the "Connected" status should appear within a minute.
          </li>
        </ol>

        {/* Webhook URL */}
        <div className="mb-4">
          <label className="form-label fw-semibold">Webhook Endpoint URL</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={WEBHOOK_ENDPOINT}
              readOnly
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
            <button
              className={`btn ${copiedUrl ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={copyUrl}
              style={{ minWidth: 80 }}
            >
              {copiedUrl ? (
                <><i className="feather icon-check me-1"></i>Copied</>
              ) : (
                <><i className="feather icon-copy me-1"></i>Copy</>
              )}
            </button>
          </div>
        </div>

        {/* Recommended events */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <label className="form-label fw-semibold mb-0">Recommended Resend Event Types</label>
            <button
              className={`btn btn-sm ${copiedEvents ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={copyEvents}
            >
              {copiedEvents ? (
                <><i className="feather icon-check me-1"></i>Copied</>
              ) : (
                <><i className="feather icon-copy me-1"></i>Copy all</>
              )}
            </button>
          </div>
          <div
            className="rounded border p-3"
            style={{ background: '#F9FAFB' }}
          >
            {RECOMMENDED_EVENTS.map((evt) => (
              <div key={evt} className="d-flex align-items-center gap-2 mb-1">
                <i className="feather icon-check-square text-success" style={{ fontSize: 14 }}></i>
                <code style={{ fontSize: 13 }}>{evt}</code>
              </div>
            ))}
          </div>
        </div>

        {/* Signing secret note */}
        <div className="alert alert-info mb-4 py-2">
          <i className="feather icon-info me-2"></i>
          The signing secret (shown by Resend after you create the webhook) must be saved as the{' '}
          <code>RESEND_WEBHOOK_SECRET</code> environment variable on the server. If you don't have
          direct access to environment variables, ask your developer to set it.
        </div>

        {/* Recent events */}
        <h6 className="mb-3" style={{ fontWeight: 700 }}>
          Recent Events
          {!loading && !error && status && (
            <span className="text-muted fw-normal ms-2" style={{ fontSize: 13 }}>
              ({status.event_count_24h} in the last 24h)
            </span>
          )}
        </h6>

        {loading ? (
          <div className="text-muted" style={{ fontSize: 14 }}>Loading…</div>
        ) : error ? null : !status?.recent_events?.length ? (
          <div
            className="rounded border text-center py-4"
            style={{ background: '#F9FAFB', color: '#8B7B73', fontSize: 14 }}
          >
            No events received yet. Complete the setup above, then send a purchase order to see
            events appear here.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ fontWeight: 600, fontSize: 13 }}>Vendor</th>
                  <th style={{ fontWeight: 600, fontSize: 13 }}>PO Number</th>
                  <th style={{ fontWeight: 600, fontSize: 13 }}>Event</th>
                  <th style={{ fontWeight: 600, fontSize: 13 }}>Time</th>
                  <th style={{ fontWeight: 600, fontSize: 13 }}>Recipient</th>
                </tr>
              </thead>
              <tbody>
                {status.recent_events.map((evt) => (
                  <tr key={evt.id}>
                    <td style={{ fontSize: 13 }}>{evt.vendor_name || '—'}</td>
                    <td style={{ fontSize: 13 }}>{evt.po_number || '—'}</td>
                    <td>
                      <span style={eventBadgeStyle(evt.event_type)}>
                        {fmtEventType(evt.event_type)}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {evt.occurred_at ? timeAgo(evt.occurred_at) : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#6B7280' }}>
                      {evt.recipient_email || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
